import React from 'react';
import { Check, Info, BarChart3, ChevronDown, ChevronRight, ListChecks, CalendarDays, AlertTriangle } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../components/ui/hover-card';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { ROLE_LABELS, COMPONENT_INFO, PLANNER_OVERRIDE_GROUPS } from './constants';
import ReleaseAnchorPicker from '../../components/estimator/ReleaseAnchorPicker';
import { HypercareScopePanel } from '../../components/estimator/HypercareScopePanel';

// Helpers for ISO ↔ week-number conversion relative to a project start date.
// Week 1 begins on the start date itself; every 7 days advances the week.
function isoToWeekNumber(iso, startIso) {
  if (!iso || !startIso) return null;
  const d = new Date(iso + 'T00:00:00');
  const s = new Date(startIso + 'T00:00:00');
  if (isNaN(d.getTime()) || isNaN(s.getTime())) return null;
  const days = Math.floor((d - s) / (1000 * 60 * 60 * 24));
  return Math.max(1, Math.floor(days / 7) + 1);
}
function weekNumberToIso(week, startIso) {
  if (!week || !startIso) return null;
  const s = new Date(startIso + 'T00:00:00');
  if (isNaN(s.getTime())) return null;
  const d = new Date(s);
  d.setDate(d.getDate() + (week - 1) * 7);
  return d.toISOString().slice(0, 10);
}
function fmtShortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Renders a single component row on the Estimator's Configure step.
 * Extracted from EstimatorPage.js (2014 lines → smaller + easier to test).
 * Takes the component and a `ctx` bundle of state + setters; zero behavior change
 * vs the inline renderComp.
 */
export function ComponentRow({ comp, ctx }) {
  const {
    selectedComponents, toggleComponent,
    hourOverrides, setHourOverrides,
    editingHour, setEditingHour,
    volumeMetrics,
    volumeOverrides, setVolumeOverrides,
    plannerStartOverrides, setPlannerStartOverrides,
    plannerWeeksOverrides, setPlannerWeeksOverrides,
    plannerBlocksOverrides, setPlannerBlocksOverrides,
    plannerReleaseAnchors, setPlannerReleaseAnchors,
    publicCloudReleases,
    results,
    hypercareScope, setHypercareScope,
    complexity,
    setDocComponent,
    setShowComponentInfo,
    getUseCaseWeight,
    getWeightIndicator,
    componentDeliverables,
    onOpenDeliverablesEditor,
    breaksByComponent,
    goalStartDate,
  } = ctx;

  const isSelected = selectedComponents[comp.name] > 0;
  const useCaseWeight = getUseCaseWeight(comp);
  const weightIndicator = getWeightIndicator(useCaseWeight);
  // Phase-1 break overlap: combine "applies_to=all" breaks with any
  // specific breaks scoped to this component name. The chip's wording
  // shifts based on whether the configured schedule actually overlaps:
  //   - true overlap → amber "⚠ overlaps {label}"
  //   - honored but no overlap (or auto-scheduled) → neutral "honors {label}"
  // so the user isn't alarmed about a break that doesn't actually hit
  // this component's window.
  const breakHits = (() => {
    if (!breaksByComponent) return [];
    const all = breaksByComponent.__ALL__ || [];
    const specific = breaksByComponent[comp.name] || [];
    return [...all, ...specific];
  })();
  // Resolve the configured schedule window (when set) so we can decide
  // overlap-vs-just-honored. Block-overrides span all blocks.
  const _ovStart = plannerStartOverrides?.[comp.name];
  const _ovWeeks = plannerWeeksOverrides?.[comp.name];
  const _ovBlocks = plannerBlocksOverrides?.[comp.name];
  let _effStart = null;
  let _effEnd = null;
  if (Array.isArray(_ovBlocks) && _ovBlocks.length > 0) {
    const valid = _ovBlocks.filter(b => b && b.start_week && b.weeks);
    if (valid.length) {
      _effStart = Math.min(...valid.map(b => b.start_week));
      _effEnd = Math.max(...valid.map(b => b.start_week + b.weeks - 1));
    }
  } else if (_ovStart && _ovWeeks) {
    _effStart = _ovStart;
    _effEnd = _ovStart + _ovWeeks - 1;
  }
  const breakHitsWithStatus = breakHits.map(h => {
    const overlaps = !!(h.weekRange && _effStart && _effEnd
      && h.weekRange.end_week >= _effStart
      && h.weekRange.start_week <= _effEnd);
    return { ...h, overlaps };
  });
  const anyOverlap = breakHitsWithStatus.some(h => h.overlaps);
  const baseHours = typeof comp.base_hours === 'object'
    ? comp.base_hours[complexity] || comp.base_hours.standard
    : comp.base_hours;
  const hasOverride = hourOverrides[comp.name] !== undefined;
  const displayHours = hasOverride ? hourOverrides[comp.name] : baseHours;
  const isEditing = editingHour === comp.name;
  const compVolMetrics = volumeMetrics.filter(m => m.parent_component === comp.name);
  const hasVolumeOption = compVolMetrics.length > 0;
  const vo = volumeOverrides[comp.name] || {};
  const isVolumeEnabled = vo.enabled || false;
  const volumeEntries = vo.entries || {};

  const toggleVolume = () => {
    setVolumeOverrides(prev => ({
      ...prev,
      [comp.name]: { ...prev[comp.name], enabled: !isVolumeEnabled, expanded: !isVolumeEnabled, entries: volumeEntries },
    }));
  };
  const setVolumeEntry = (subName, val) => {
    setVolumeOverrides(prev => ({
      ...prev,
      [comp.name]: { ...prev[comp.name], enabled: true, entries: { ...(prev[comp.name]?.entries || {}), [subName]: parseInt(val) || 0 } },
    }));
  };
  const toggleVolumeExpand = () => {
    setVolumeOverrides(prev => ({
      ...prev,
      [comp.name]: { ...prev[comp.name], expanded: !(prev[comp.name]?.expanded) },
    }));
  };
  const volumeTotal = hasVolumeOption && isVolumeEnabled
    ? compVolMetrics.reduce((sum, m) => {
        const vol = volumeEntries[m.sub_component] || 0;
        const rate = m[`${complexity}_hrs_per_vol`] || m.standard_hrs_per_vol || 0;
        return sum + (vol * rate);
      }, 0)
    : 0;

  return (
    <div
      className={`p-4 rounded-xl border transition-all ${
        isSelected ? 'border-[#0F2040] bg-slate-50 shadow-sm' : 'border-slate-200 hover:border-slate-300'
      }`}
      data-testid={`component-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => toggleComponent(comp.name)}
            className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors shrink-0 ${
              isSelected ? 'bg-[#0F2040] border-[#0F2040]' : 'border-slate-300 hover:border-[#0F2040]'
            }`}
          >
            {isSelected && <Check className="w-4 h-4 text-white" />}
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-medium text-[#0F2040]">{comp.display_name || comp.name}</h4>
              {comp.sub_group === 'Learning Intelligence' && (
                <span
                  className="shrink-0 inline-flex items-center rounded-full bg-[#4ABFCF]/15 text-[#0F6B78] border border-[#4ABFCF]/40 text-[9px] font-semibold px-1.5 py-0 leading-4"
                  title="Learning Intelligence 'I' application"
                  data-testid={`i-app-badge-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  ‘I’ app
                </span>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); setShowComponentInfo({ name: comp.name, display_name: comp.display_name, group: comp.group, base_hours: comp.base_hours, ...(COMPONENT_INFO[comp.name] || {}) }); }}
                className="text-slate-400 hover:text-[#4ABFCF] transition-colors cursor-help"
                data-testid={`component-info-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <Info className="w-3.5 h-3.5" />
              </button>
              {isSelected && onOpenDeliverablesEditor && (() => {
                const list = componentDeliverables?.[comp.name] || [];
                const milestones = list.filter(d => d.is_milestone).length;
                const hasAny = list.length > 0;
                return (
                  <button
                    onClick={(e) => { e.stopPropagation(); onOpenDeliverablesEditor(comp.name); }}
                    className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-md border transition-colors ${
                      hasAny
                        ? 'border-cyan-300 bg-cyan-50 text-cyan-700 hover:bg-cyan-100'
                        : 'border-slate-200 text-slate-500 hover:border-cyan-300 hover:text-cyan-700'
                    }`}
                    data-testid={`component-deliverables-btn-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}
                    title="Add detailed deliverables for this component"
                  >
                    <ListChecks className="w-3 h-3" />
                    Details{hasAny ? ` (${list.length}${milestones ? ` · ${milestones} milestone${milestones === 1 ? '' : 's'}` : ''})` : ''}
                  </button>
                );
              })()}
              {comp.required && (
                <HoverCard openDelay={150} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#0F2040] text-white cursor-help"
                      data-testid={`badge-required-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}>
                      Required
                    </span>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-72 text-xs" side="top">
                    <div className="font-semibold text-[#0F2040] mb-1">Why is this required?</div>
                    <p className="text-slate-600 leading-relaxed">
                      {comp.required_reason
                        || COMPONENT_INFO[comp.name]?.hours_rationale
                        || 'This is a mandatory component for every Seertech LMS implementation — it cannot be removed from the scope.'}
                    </p>
                    <button type="button" onClick={() => setDocComponent(comp)}
                      className="mt-2 text-[10px] text-[#4ABFCF] hover:text-[#3AAAB9] underline-offset-2 hover:underline"
                      data-testid={`learn-more-required-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}>
                      Learn more →
                    </button>
                  </HoverCardContent>
                </HoverCard>
              )}
              {!comp.required && comp.recommended && (
                <HoverCard openDelay={150} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 cursor-help"
                      data-testid={`badge-recommended-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}>
                      Recommended
                    </span>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-72 text-xs" side="top">
                    <div className="font-semibold text-[#0F2040] mb-1">Why is this recommended?</div>
                    <p className="text-slate-600 leading-relaxed">
                      {comp.recommended_reason
                        || COMPONENT_INFO[comp.name]?.hours_rationale
                        || 'This is a recommended default for most implementations. You can remove it if the client has decided to de-scope.'}
                    </p>
                    <button type="button" onClick={() => setDocComponent(comp)}
                      className="mt-2 text-[10px] text-[#4ABFCF] hover:text-[#3AAAB9] underline-offset-2 hover:underline"
                      data-testid={`learn-more-recommended-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}>
                      Learn more →
                    </button>
                  </HoverCardContent>
                </HoverCard>
              )}
              {hasOverride && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Custom</span>}
              {weightIndicator && !comp.required && !comp.recommended && !hasOverride && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${weightIndicator.color}`}>{weightIndicator.label}</span>
              )}
              {isSelected && breakHits.length > 0 && (
                <HoverCard openDelay={150} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ring-1 cursor-help ${
                        anyOverlap
                          ? 'bg-amber-100 text-amber-800 ring-amber-300/80'
                          : 'bg-slate-100 text-slate-600 ring-slate-200'
                      }`}
                      data-testid={`component-break-warning-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {anyOverlap ? '⚠ overlaps' : 'honors'} {breakHits.length === 1 ? breakHits[0].label : `${breakHits.length} breaks`}
                    </span>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-72 text-xs" side="top">
                    <div className="font-semibold text-[#0F2040] mb-1">
                      {anyOverlap ? 'Schedule conflict with breaks' : 'Honored breaks for this component'}
                    </div>
                    <ul className="space-y-1 text-slate-600 leading-relaxed">
                      {breakHitsWithStatus.map((bh, i) => (
                        <li key={`${bh.id}-${i}`} className="flex items-center gap-1.5">
                          <span className={`w-1 h-1 rounded-full shrink-0 ${bh.overlaps ? 'bg-amber-500' : 'bg-slate-400'}`} />
                          <span className={bh.overlaps ? 'text-amber-800 font-medium' : ''}>{bh.label}</span>
                          {bh.weekRange && (
                            <span className="text-slate-400 ml-1">
                              W{bh.weekRange.start_week}-W{bh.weekRange.end_week}{bh.overlaps ? ' · overlaps' : ''}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-[10px] text-slate-400">
                      {anyOverlap
                        ? 'The Planner will pause this component during the windows that overlap and extend its end date accordingly.'
                        : 'These breaks apply if this component is scheduled into them. No overlap detected with the current schedule.'}
                    </p>
                  </HoverCardContent>
                </HoverCard>
              )}
            </div>
            <div className="flex items-center gap-3 gap-y-1.5 mt-1 text-sm text-slate-500 flex-wrap">
              {isEditing ? (
                <div className="flex items-center gap-1.5">
                  <input
                    type="number" min="0" autoFocus defaultValue={displayHours}
                    className="w-16 h-6 text-sm border border-[#4ABFCF] rounded px-1.5 text-center text-[#0F2040] focus:outline-none focus:ring-1 focus:ring-[#4ABFCF]"
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value);
                      if (!isNaN(val) && val > 0 && val !== baseHours) {
                        setHourOverrides(prev => ({ ...prev, [comp.name]: val }));
                      } else if (val === baseHours) {
                        setHourOverrides(prev => { const n = { ...prev }; delete n[comp.name]; return n; });
                      }
                      setEditingHour(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.target.blur();
                      if (e.key === 'Escape') setEditingHour(null);
                    }}
                    data-testid={`hour-override-input-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}
                  />
                  <span className="text-xs text-slate-400">hrs</span>
                  {hasOverride && (
                    <button
                      // Use onMouseDown + preventDefault to fire before the
                      // input's onBlur — without this, the blur handler
                      // re-writes the override using the input's current
                      // (still-overridden) value, defeating the reset.
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setHourOverrides(prev => { const n = { ...prev }; delete n[comp.name]; return n; });
                        setEditingHour(null);
                      }}
                      className="text-xs text-red-400 hover:text-red-600 ml-1"
                      data-testid={`hour-reset-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      reset
                    </button>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => !isVolumeEnabled && setEditingHour(comp.name)}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border transition-all ${
                    isVolumeEnabled
                      ? 'bg-slate-100 border-slate-200 cursor-not-allowed opacity-50'
                      : 'bg-white border-slate-300 hover:border-[#4ABFCF] hover:bg-[#4ABFCF]/5 cursor-pointer group/hrs'
                  }`}
                  title={isVolumeEnabled ? 'Hours driven by volume metrics' : 'Click to override hours'}
                  disabled={isVolumeEnabled}
                  data-testid={`hour-display-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <span className={`text-sm font-medium ${isVolumeEnabled ? 'text-slate-400' : 'text-[#0F2040]'}`}>{displayHours}h</span>
                  {!isVolumeEnabled && (
                    <svg className="w-3 h-3 text-slate-400 group-hover/hrs:text-[#4ABFCF] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  )}
                </button>
              )}
              <span>&middot;</span>
              <Badge variant="secondary" className="text-xs">{ROLE_LABELS[comp.role] || comp.role}</Badge>
              {isSelected && PLANNER_OVERRIDE_GROUPS.has(comp.group) && (() => {
                const blocks = plannerBlocksOverrides ? plannerBlocksOverrides[comp.name] : null;
                const hasBlocks = Array.isArray(blocks) && blocks.length > 0;
                const block1 = hasBlocks ? (blocks[0] || {}) : null;
                const block2 = hasBlocks && blocks.length > 1 ? (blocks[1] || {}) : null;
                const compSlug = comp.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

                const updateBlock = (idx, patch) => {
                  setPlannerBlocksOverrides(prev => {
                    const next = { ...(prev || {}) };
                    const list = (next[comp.name] || [{}, {}]).map(b => ({ ...b }));
                    while (list.length < 2) list.push({});
                    list[idx] = { ...list[idx], ...patch };
                    next[comp.name] = list;
                    return next;
                  });
                };

                const startValue = hasBlocks ? (block1.start_week || '') : (plannerStartOverrides[comp.name] || '');
                const weeksValue = hasBlocks ? (block1.weeks || '') : (plannerWeeksOverrides[comp.name] || '');

                const onStartChange = (raw) => {
                  const v = raw ? parseInt(raw) : null;
                  if (hasBlocks) {
                    updateBlock(0, { start_week: v && v > 0 ? v : 0 });
                  } else {
                    setPlannerStartOverrides(prev => {
                      const n = { ...prev };
                      if (v && v > 0) n[comp.name] = v; else delete n[comp.name];
                      return n;
                    });
                  }
                };
                const onWeeksChange = (raw) => {
                  const v = raw ? parseInt(raw) : null;
                  if (hasBlocks) {
                    updateBlock(0, { weeks: v && v > 0 ? v : 0 });
                  } else {
                    setPlannerWeeksOverrides(prev => {
                      const n = { ...prev };
                      if (v && v > 0) n[comp.name] = v; else delete n[comp.name];
                      return n;
                    });
                  }
                };

                const enableBreak = () => {
                  const s = plannerStartOverrides[comp.name] || 1;
                  const w = plannerWeeksOverrides[comp.name] || 4;
                  setPlannerBlocksOverrides(prev => ({
                    ...(prev || {}),
                    [comp.name]: [{ start_week: s, weeks: w }, { start_week: 0, weeks: 0 }],
                  }));
                  setPlannerStartOverrides(prev => { const n = { ...prev }; delete n[comp.name]; return n; });
                  setPlannerWeeksOverrides(prev => { const n = { ...prev }; delete n[comp.name]; return n; });
                };
                const removeBreak = () => {
                  // Demote block 1 back to legacy start/weeks. We capture the
                  // values up-front (block1 is a stale closure ref, but its
                  // primitive fields are stable through this single click).
                  const restoredStart = block1 && block1.start_week ? block1.start_week : (plannerStartOverrides[comp.name] || 1);
                  const restoredWeeks = block1 && block1.weeks ? block1.weeks : (plannerWeeksOverrides[comp.name] || 4);
                  setPlannerStartOverrides(prev => ({ ...prev, [comp.name]: restoredStart }));
                  setPlannerWeeksOverrides(prev => ({ ...prev, [comp.name]: restoredWeeks }));
                  setPlannerBlocksOverrides(prev => {
                    const n = { ...(prev || {}) };
                    delete n[comp.name];
                    return n;
                  });
                };

                const horizon = results?.projected_weeks;

                // ── Calendar / smart break-overlap helpers (Phase 2) ──
                // When `goalStartDate` is set on the parent estimate, week
                // numbers map to real dates so we can offer a date picker
                // and detect collisions with honored break windows.
                const startWeekNum = parseInt(startValue) || null;
                const weeksNum = parseInt(weeksValue) || null;
                const endWeekNum = (startWeekNum && weeksNum) ? startWeekNum + weeksNum - 1 : null;
                const startIso = startWeekNum && goalStartDate ? weekNumberToIso(startWeekNum, goalStartDate) : null;
                const endIso = endWeekNum && goalStartDate ? weekNumberToIso(endWeekNum + 1, goalStartDate) : null; // exclusive end
                const endIsoInclusive = endWeekNum && goalStartDate ? weekNumberToIso(endWeekNum, goalStartDate) : null;
                const dateHint = startIso && endIsoInclusive
                  ? `≈ ${fmtShortDate(startIso)} – ${fmtShortDate(endIsoInclusive)}`
                  : null;

                // Honored breaks for this component, with week-ranges resolved.
                const honoredBreaks = (() => {
                  if (!breaksByComponent) return [];
                  const all = breaksByComponent.__ALL__ || [];
                  const specific = breaksByComponent[comp.name] || [];
                  return [...all, ...specific].filter(b => b.weekRange);
                })();
                // First break that the configured [start, end] window collides with.
                const collidingBreak = (startWeekNum && endWeekNum) ? honoredBreaks.find(b => {
                  const r = b.weekRange;
                  return r.end_week >= startWeekNum && r.start_week <= endWeekNum;
                }) : null;
                const earliestResume = collidingBreak ? collidingBreak.weekRange.end_week + 1 : null;
                // Largest weeks-value that fits before the colliding break starts.
                const maxWeeksBefore = collidingBreak && startWeekNum && startWeekNum < collidingBreak.weekRange.start_week
                  ? collidingBreak.weekRange.start_week - startWeekNum
                  : null;

                const onPickStartDate = (date) => {
                  if (!date) return;
                  const iso = date.toISOString().slice(0, 10);
                  const wk = isoToWeekNumber(iso, goalStartDate);
                  if (wk) onStartChange(String(wk));
                };
                const onPickEndDate = (date) => {
                  if (!date || !startWeekNum) return;
                  const iso = date.toISOString().slice(0, 10);
                  const wk = isoToWeekNumber(iso, goalStartDate);
                  if (wk) {
                    const newWeeks = Math.max(1, wk - startWeekNum + 1);
                    onWeeksChange(String(newWeeks));
                  }
                };

                return (
                  <>
                    <span>&middot;</span>
                    <div className="inline-flex items-center gap-1.5" title="Optional: choose which week this component starts. Leave blank to auto-schedule.">
                      <span className="text-xs text-slate-400">Start</span>
                      <input
                        type="number" min="1" placeholder="Auto"
                        value={startValue}
                        onChange={(e) => onStartChange(e.target.value)}
                        className="w-14 h-6 text-xs text-center border border-slate-300 rounded bg-white text-[#0F2040] focus:border-[#4ABFCF] focus:outline-none focus:ring-1 focus:ring-[#4ABFCF]"
                        data-testid={`planner-start-week-${compSlug}`}
                      />
                      <span className="text-xs text-slate-400">wk</span>
                      {goalStartDate && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-[#4ABFCF] transition-colors"
                              title="Pick start date — converts to week number"
                              data-testid={`planner-start-date-btn-${compSlug}`}
                            >
                              <CalendarDays className="w-3.5 h-3.5" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={startIso ? new Date(startIso + 'T00:00:00') : undefined}
                              onSelect={onPickStartDate}
                              initialFocus
                              data-testid={`planner-start-date-cal-${compSlug}`}
                            />
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                    <div className="inline-flex items-center gap-1.5" title="Optional: how many weeks this component runs for. The same total effort is redistributed evenly across this window. Leave blank to auto-size.">
                      <span className="text-xs text-slate-400">Weeks</span>
                      <input
                        type="number" min="1" placeholder="Auto"
                        value={weeksValue}
                        onChange={(e) => onWeeksChange(e.target.value)}
                        className="w-14 h-6 text-xs text-center border border-slate-300 rounded bg-white text-[#0F2040] focus:border-[#4ABFCF] focus:outline-none focus:ring-1 focus:ring-[#4ABFCF]"
                        data-testid={`planner-weeks-${compSlug}`}
                      />
                      {goalStartDate && startWeekNum && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-[#4ABFCF] transition-colors"
                              title="Pick end date — converts to a weeks count"
                              data-testid={`planner-end-date-btn-${compSlug}`}
                            >
                              <CalendarDays className="w-3.5 h-3.5" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={endIsoInclusive ? new Date(endIsoInclusive + 'T00:00:00') : undefined}
                              onSelect={onPickEndDate}
                              disabled={(d) => {
                                if (!startIso) return false;
                                return d < new Date(startIso + 'T00:00:00');
                              }}
                              initialFocus
                              data-testid={`planner-end-date-cal-${compSlug}`}
                            />
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                    {dateHint && (
                      <span
                        className="text-[10px] text-slate-500 tabular-nums"
                        data-testid={`planner-date-hint-${compSlug}`}
                        title={`Maps to ${fmtShortDate(startIso)} → ${fmtShortDate(endIsoInclusive)} when the project starts ${fmtShortDate(goalStartDate)}.`}
                      >
                        {dateHint}
                      </span>
                    )}
                    {!hasBlocks && (() => {
                      const w = plannerWeeksOverrides[comp.name];
                      const s = plannerStartOverrides[comp.name] || 1;
                      if (!w || !horizon || s + w - 1 <= horizon) return null;
                      const fitted = Math.max(1, horizon - s + 1);
                      return (
                        <span
                          className="text-[10px] text-amber-700 font-medium"
                          data-testid={`planner-weeks-clamp-${compSlug}`}
                          title={`Project horizon is ~${horizon} weeks. Your ${w}-week window starting at W${s} will be trimmed.`}
                        >
                          will be trimmed to {fitted}w
                        </span>
                      );
                    })()}
                    {!hasBlocks && collidingBreak && (
                      <div
                        className="basis-full flex items-center gap-1.5 mt-1 text-[10px] text-amber-800 bg-amber-50/80 border border-amber-200 rounded-md px-2 py-1"
                        data-testid={`planner-break-collision-${compSlug}`}
                      >
                        <AlertTriangle className="w-3 h-3 shrink-0" />
                        <span className="font-medium">Overlaps {collidingBreak.label}</span>
                        <span className="text-amber-700">(W{collidingBreak.weekRange.start_week}-W{collidingBreak.weekRange.end_week}).</span>
                        {earliestResume && earliestResume <= (horizon || 999) && (
                          <button
                            type="button"
                            onClick={() => onStartChange(String(earliestResume))}
                            className="font-semibold underline underline-offset-2 hover:text-amber-900"
                            title={`Move start to W${earliestResume} (right after the break)`}
                            data-testid={`planner-break-resume-after-${compSlug}`}
                          >
                            → Resume at W{earliestResume}
                          </button>
                        )}
                        {maxWeeksBefore !== null && maxWeeksBefore > 0 && (
                          <button
                            type="button"
                            onClick={() => onWeeksChange(String(maxWeeksBefore))}
                            className="font-semibold underline underline-offset-2 hover:text-amber-900"
                            title={`Trim duration to ${maxWeeksBefore} week${maxWeeksBefore === 1 ? '' : 's'} so it ends before the break starts`}
                            data-testid={`planner-break-fit-before-${compSlug}`}
                          >
                            Fit before: max {maxWeeksBefore}w
                          </button>
                        )}
                      </div>
                    )}
                    {!hasBlocks && (
                      <button
                        type="button"
                        onClick={enableBreak}
                        className="text-[10px] text-slate-400 hover:text-cyan-700 underline-offset-2 hover:underline ml-1"
                        title="Split this component into two blocks (pause + resume)"
                        data-testid={`planner-add-break-${compSlug}`}
                      >
                        + break
                      </button>
                    )}
                    {hasBlocks && (
                      <div className="basis-full flex items-center gap-2 ml-1 mt-0.5 pl-2 pr-2 py-1 rounded-md bg-amber-50/60 border border-amber-200/70" data-testid={`planner-block-2-${compSlug}`}>
                        <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide shrink-0">Resume</span>
                        <div className="inline-flex items-center gap-1.5" title="The week this component resumes after the pause.">
                          <span className="text-xs text-amber-700">at</span>
                          <input
                            type="number" min="1" placeholder="W"
                            value={block2?.start_week || ''}
                            onChange={(e) => updateBlock(1, { start_week: e.target.value ? parseInt(e.target.value) : 0 })}
                            className="w-14 h-6 text-xs text-center border border-amber-300 rounded bg-white text-[#0F2040] focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                            data-testid={`planner-resume-week-${compSlug}`}
                          />
                          <span className="text-xs text-amber-700">wk</span>
                        </div>
                        <div className="inline-flex items-center gap-1.5" title="How many weeks the resumed block runs for.">
                          <span className="text-xs text-amber-700">for</span>
                          <input
                            type="number" min="1" placeholder="W"
                            value={block2?.weeks || ''}
                            onChange={(e) => updateBlock(1, { weeks: e.target.value ? parseInt(e.target.value) : 0 })}
                            className="w-14 h-6 text-xs text-center border border-amber-300 rounded bg-white text-[#0F2040] focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                            data-testid={`planner-resume-weeks-${compSlug}`}
                          />
                          <span className="text-xs text-amber-700">wks</span>
                        </div>
                        <button
                          type="button"
                          onClick={removeBreak}
                          className="ml-auto text-[10px] text-amber-700 hover:text-red-600 underline-offset-2 hover:underline shrink-0"
                          title="Remove the pause and resume schedule"
                          data-testid={`planner-remove-break-${compSlug}`}
                        >
                          × remove break
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      {hasVolumeOption && isSelected && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <button
            onClick={toggleVolume}
            className={`text-xs flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-all ${
              isVolumeEnabled
                ? 'bg-[#14B8A6]/10 text-[#14B8A6] border border-[#14B8A6]/30'
                : 'text-slate-400 hover:text-slate-600 border border-transparent hover:border-slate-200'
            }`}
            data-testid={`volume-toggle-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}
          >
            <BarChart3 className="w-3 h-3" />
            {isVolumeEnabled ? 'Volume-based' : 'Use volume metrics'}
            {isVolumeEnabled && volumeTotal > 0 && (
              <span className="font-semibold ml-1">({volumeTotal.toFixed(1)}h)</span>
            )}
          </button>
          {isVolumeEnabled && (
            <div className="mt-2 space-y-1.5">
              <button onClick={toggleVolumeExpand} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
                {vo.expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                {compVolMetrics.length} sub-components
              </button>
              {vo.expanded && compVolMetrics.map(m => (
                <div key={m.sub_component} className="flex items-center gap-2 pl-4 text-xs" data-testid={`volume-entry-${m.sub_component.replace(/\s+/g, '-').toLowerCase()}`}>
                  <span className="text-slate-600 flex-1 truncate" title={m.sub_component}>{m.sub_component}</span>
                  <span className="text-slate-400 whitespace-nowrap">{m[`${complexity}_hrs_per_vol`] || m.standard_hrs_per_vol} hrs/ea</span>
                  <span className="text-slate-300">×</span>
                  <input
                    type="number" min="0"
                    value={volumeEntries[m.sub_component] || ''}
                    placeholder="0"
                    onChange={(e) => setVolumeEntry(m.sub_component, e.target.value)}
                    className="w-16 h-6 text-xs border border-slate-200 rounded px-1.5 text-center text-[#0F2040] focus:outline-none focus:ring-1 focus:ring-[#14B8A6] focus:border-[#14B8A6]"
                  />
                  {(volumeEntries[m.sub_component] || 0) > 0 && (
                    <span className="text-[#14B8A6] font-medium whitespace-nowrap">
                      = {((volumeEntries[m.sub_component] || 0) * (m[`${complexity}_hrs_per_vol`] || m.standard_hrs_per_vol)).toFixed(1)}h
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isSelected && comp.group === 'Integrations' && (() => {
        // v258.6 — release-anchored scheduling for Integration components.
        // Selecting a release + lead-weeks computes planner_start_week and
        // planner_weeks via the shared ReleaseAnchorPicker so the component
        // finishes ON the release date. Mirrors the New Dev / SOW
        // Integration picker for consistency.
        const anchor = plannerReleaseAnchors?.[comp.name] || {};
        return (
          <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`release-anchor-wrap-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}>
            <ReleaseAnchorPicker
              releases={publicCloudReleases || []}
              projectStartDate={goalStartDate || null}
              anchorReleaseId={anchor.release_id || null}
              anchorLeadWeeks={anchor.lead_weeks || null}
              testidPrefix={`comp-anchor-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}
              onAnchor={({ release_id, lead_weeks, start_week, weeks }) => {
                setPlannerReleaseAnchors((prev) => ({
                  ...prev,
                  [comp.name]: { release_id, lead_weeks },
                }));
                if (start_week) {
                  setPlannerStartOverrides((prev) => ({ ...prev, [comp.name]: start_week }));
                }
                if (weeks) {
                  setPlannerWeeksOverrides((prev) => ({ ...prev, [comp.name]: weeks }));
                }
              }}
              onClear={() => {
                setPlannerReleaseAnchors((prev) => {
                  const n = { ...prev };
                  delete n[comp.name];
                  return n;
                });
              }}
            />
          </div>
        );
      })()}

      {isSelected && comp.name === 'Hypercare Support' && (
        <HypercareScopePanel
          scope={hypercareScope}
          onChange={setHypercareScope}
          presetHours={baseHours}
        />
      )}
    </div>
  );
}
